import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { decryptBookingId } from '../utils/crypto';
import { uploadDocument } from '../api/bookingApi';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.tif', '.tiff', '.doc', '.docx', '.xls', '.xlsx'];

function DocUploadPage() {
    const [searchParams] = useSearchParams();
    const bookingId = searchParams.get('bookingId');
    const decryptedId = decryptBookingId(bookingId) || bookingId;
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const processFiles = (selectedFiles) => {
        const validFiles = [];
        const rejectedMessages = [];

        selectedFiles.forEach(file => {
            const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                rejectedMessages.push(`${file.name}: Invalid file type`);
            } else if (file.size > MAX_FILE_SIZE) {
                rejectedMessages.push(`${file.name}: File too large (>100MB)`);
            } else {
                validFiles.push(file);
            }
        });

        if (rejectedMessages.length > 0) {
            alert(`Some files were rejected:\n${rejectedMessages.join('\n')}`);
        }

        setFiles(prev => [...prev, ...validFiles]);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            processFiles(Array.from(e.target.files));
        }
        // reset the input value so the same file can be selected again if needed
        e.target.value = null; 
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = error => reject(error);
        });
    };

    const handleUpload = async () => {
        if (!decryptedId) {
            alert('Cannot upload without a valid Person ID.');
            return;
        }
        if (files.length === 0) return;

        setIsUploading(true);
        setUploadStatus(null);
        let successCount = 0;
        let failCount = 0;

        for (const file of files) {
            try {
                const base64Data = await convertToBase64(file);
                const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                
                await uploadDocument(decryptedId, ext, base64Data);
                successCount++;
            } catch (err) {
                console.error("Upload failed for file:", file.name, err);
                failCount++;
            }
        }

        setIsUploading(false);
        if (failCount === 0) {
            setUploadStatus({ type: 'success', message: `Successfully uploaded ${successCount} document(s)!` });
            setFiles([]); // clear queue on full success
        } else {
            setUploadStatus({ type: 'error', message: `Uploaded ${successCount} document(s). Failed to upload ${failCount} document(s).` });
        }
    };

    return (
        <div style={{ padding: '60px 20px', minHeight: 'calc(100vh - 200px)', backgroundColor: '#f0f4f8' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                    <h1 style={{ color: '#000', marginBottom: '20px', fontSize: '1.8rem', fontWeight: 'bold' }}>Securely upload your documents</h1>
                    <p style={{ color: '#333', marginBottom: '5px', fontSize: '1.1rem' }}>Please upload the documents requested for your case. Your files will be sent directly and securely to your case.</p>
                    <p style={{ color: '#333', fontSize: '1.1rem', fontStyle: 'italic' }}>Your link is unique to you. These records will be linked to your case – no need to include your name or case number.</p>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', justifyContent: 'center' }}>
                    {/* Left Column */}
                    <div style={{ flex: '1 1 400px', maxWidth: '450px', color: '#333' }}>
                        <p style={{ marginBottom: '10px', fontSize: '1.05rem' }}>Required documents:</p>
                        <ul style={{ listStyleType: 'none', padding: 0, margin: '0 0 30px 0', fontSize: '1.05rem' }}>
                            <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <span style={{ marginRight: '10px', fontSize: '1.2em', lineHeight: '1' }}>❑</span>
                                <span>Physical examination completed by a healthcare provider</span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <span style={{ marginRight: '10px', fontSize: '1.2em', lineHeight: '1' }}>❑</span>
                                <span>MMR immunity documentation (positive titer <strong>OR</strong> vaccination records)</span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <span style={{ marginRight: '10px', fontSize: '1.2em', lineHeight: '1' }}>❑</span>
                                <span>TB screening (PPD <strong>OR</strong> QuantiFERON-TB Gold)</span>
                            </li>
                            <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <span style={{ marginRight: '10px', fontSize: '1.2em', lineHeight: '1' }}>❑</span>
                                <span>PPL Participant Form (completed <strong>and</strong> signed)</span>
                            </li>
                        </ul>
                        <p style={{ fontStyle: 'italic', fontSize: '0.95rem', marginBottom: '5px' }}>Exams and lab results must be dated within the past 365 days.</p>
                        <p style={{ fontStyle: 'italic', fontSize: '0.95rem' }}>Incomplete or missing documentation may delay processing and approval.</p>
                    </div>

                    {/* Right Column */}
                    <div style={{ flex: '1 1 400px', maxWidth: '450px', backgroundColor: '#fff', padding: '20px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <div 
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            style={{ 
                                padding: '40px 20px',
                                border: isDragActive ? '2px dashed #0056b3' : '2px dashed #ccc',
                                borderRadius: '4px',
                                backgroundColor: isDragActive ? '#eef5ff' : '#fff',
                                transition: 'all 0.3s ease',
                                textAlign: 'center',
                                marginBottom: '20px'
                            }}
                        >
                            <input 
                                type="file" 
                                multiple 
                                id="doc-upload-input"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <div style={{ marginBottom: '15px', color: '#555', fontSize: '1.1rem' }}>
                                Drag files here, or
                            </div>
                            <label htmlFor="doc-upload-input" style={{
                                display: 'inline-block',
                                padding: '10px 20px',
                                backgroundColor: '#0056b3',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                marginBottom: '25px'
                            }}>
                                Browse your device
                            </label>
                            <div style={{ fontSize: '0.85em', color: '#777' }}>
                                Accepted formats: PDF, TIF, JPG, DOC, DOCX, XLS, XLSX. Max 100 MB per file
                            </div>
                        </div>

                        {files.length > 0 && (
                            <div style={{ textAlign: 'left', backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '4px', marginBottom: '20px' }}>
                                <small style={{ fontWeight: 'bold' }}>Selected {files.length} file(s):</small>
                                <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
                                    {files.map((f, index) => (
                                        <li key={index} style={{ fontSize: '0.9em', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '4px' }}>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }} title={f.name}>
                                                {f.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                style={{ border: 'none', background: 'none', color: '#d9534f', cursor: 'pointer', fontSize: '1.5em', lineHeight: '1', padding: '0 5px' }}
                                                aria-label="Remove file"
                                            >
                                                ×
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {uploadStatus && (
                            <div style={{
                                padding: '10px', 
                                marginBottom: '20px', 
                                borderRadius: '4px',
                                backgroundColor: uploadStatus.type === 'success' ? '#dff0d8' : '#f2dede',
                                color: uploadStatus.type === 'success' ? '#3c763d' : '#a94442'
                            }}>
                                {uploadStatus.message}
                            </div>
                        )}

                        <button 
                            onClick={handleUpload}
                            disabled={files.length === 0 || isUploading || !decryptedId}
                            style={{
                                padding: '12px 30px',
                                backgroundColor: files.length === 0 || isUploading || !decryptedId ? '#ccc' : '#0056b3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '1.1em',
                                cursor: files.length === 0 || isUploading || !decryptedId ? 'not-allowed' : 'pointer',
                                width: '100%'
                            }}
                        >
                            {isUploading ? 'Uploading...' : 'Upload Selected Files'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DocUploadPage;
